import React, { useEffect, useRef } from "react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  children?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Move focus into the dialog on open, return it to the trigger on close
    // (WCAG 2.4.3), and close on Escape (2.1.2).
    openerRef.current = document.activeElement as HTMLElement;
    containerRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onCancel) {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      openerRef.current?.focus();
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Confirmation"
    >
      <div className="modal-container" ref={containerRef} tabIndex={-1}>
        <div className="modal-message">{children}</div>
        <div className="modal-actions">
          {!!onConfirm && (
            <button className="modal-confirm-button" onClick={onConfirm}>
              Confirm
            </button>
          )}
          {!!onCancel && (
            <button className="modal-cancel-button" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
